import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, LinkSimple, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-400/50 disabled:opacity-50";

// One account ↔ one QuickBooks customer: shows the link, syncs (match or create),
// AI-suggests a match, searches QuickBooks by hand, unlinks. Used on the account page
// and inside Integrations → Customer matching.
export const QboCustomerLinker = ({ customerId, customerName, connected, compact = false, onChange }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState("");
  const [qboQ, setQboQ] = useState("");
  const [qboResults, setQboResults] = useState([]);
  const [aiResult, setAiResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/qbo/customers/${customerId}/link`);
      setLink(data);
      onChange?.(data);
    } catch {
      setLink(null);
    }
  }, [customerId, onChange]);

  useEffect(() => {
    setQboQ("");
    setQboResults([]);
    setAiResult(null);
    load();
  }, [load]);

  const fail = (err, fallback) =>
    toast.error(formatApiErrorDetail(err.response?.data?.detail) || fallback);

  const sync = async () => {
    setBusy("sync");
    try {
      const { data } = await api.post(`/qbo/customers/${customerId}/sync`);
      setLink(data);
      onChange?.(data);
      toast.success(
        data.created
          ? `Created "${data.qbo_name || customerName}" in QuickBooks`
          : `Matched to "${data.qbo_name || data.qbo_id}" in QuickBooks`,
      );
    } catch (err) {
      fail(err, "QuickBooks sync failed");
    }
    setBusy("");
  };

  const aiMatch = async () => {
    setBusy("ai");
    setAiResult(null);
    try {
      const { data } = await api.post("/qbo/customers/ai-match", { app_customer_id: customerId });
      if (!data.qbo_connected) toast.error("Connect QuickBooks first");
      else {
        setAiResult(data);
        if (!(data.candidates || []).length) toast("No close matches found in QuickBooks");
      }
    } catch (err) {
      fail(err, "AI match failed");
    }
    setBusy("");
  };

  const searchQbo = async (v) => {
    setQboQ(v);
    if (!v.trim()) {
      setQboResults([]);
      return;
    }
    try {
      const { data } = await api.get(`/qbo/customers?q=${encodeURIComponent(v.trim())}`);
      setQboResults((data.customers || []).slice(0, 8));
    } catch {
      setQboResults([]);
    }
  };

  const linkTo = async (qbo) => {
    setBusy("link");
    try {
      await api.post("/qbo/mapping/customer", {
        app_customer_id: customerId,
        qbo_id: qbo.id,
        name: qbo.name,
      });
      toast.success(`Linked ${customerName} → ${qbo.name}`);
      setQboQ("");
      setQboResults([]);
      setAiResult(null);
      await load();
    } catch (err) {
      fail(err, "Could not link");
    }
    setBusy("");
  };

  const unlink = async () => {
    setBusy("unlink");
    try {
      await api.post("/qbo/mapping/customer", { app_customer_id: customerId, qbo_id: "" });
      await api.delete(`/qbo/customers/${customerId}/link`).catch(() => {});
      toast.success("Unlinked from QuickBooks");
      await load();
    } catch (err) {
      fail(err, "Could not unlink");
    }
    setBusy("");
  };

  if (!link) return null;
  const on = connected ?? link.connected;

  if (link.linked)
    return (
      <div data-testid="qbo-linker">
        <div
          className="flex items-center justify-between gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-300"
          data-testid="qbo-linker-linked"
        >
          <span className="min-w-0 truncate">
            <LinkSimple size={14} className="mr-1 inline" />
            {link.qbo_name || `QuickBooks #${link.qbo_id}`}
            <span className="ml-1 text-[10px] uppercase tracking-widest text-emerald-500/70">
              {link.source === "manual" ? "linked by hand" : link.created_in_qbo ? "created" : "matched"}
            </span>
          </span>
          {isAdmin && (
            <button
              data-testid="qbo-linker-unlink"
              onClick={unlink}
              disabled={!!busy}
              className="shrink-0 text-xs font-semibold text-red-400 hover:underline disabled:opacity-40"
            >
              Unlink
            </button>
          )}
        </div>
      </div>
    );

  return (
    <div data-testid="qbo-linker" className="space-y-2">
      {link.error && (
        <p className="text-xs text-red-400" data-testid="qbo-linker-error">
          Last sync failed: {link.error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          data-testid="qbo-linker-sync"
          onClick={sync}
          disabled={!on || !!busy}
          className="h-9 gap-1.5 bg-emerald-500 text-[10px] font-bold uppercase tracking-widest text-zinc-950 hover:bg-emerald-400 disabled:opacity-40"
        >
          <ArrowsClockwise size={14} weight="bold" className={busy === "sync" ? "animate-spin" : ""} />
          {busy === "sync" ? "Syncing…" : "Sync to QuickBooks"}
        </Button>
        {isAdmin && (
          <Button
            data-testid="qbo-linker-ai"
            onClick={aiMatch}
            disabled={!on || !!busy}
            variant="outline"
            className="h-9 gap-1.5 border-indigo-500/40 bg-indigo-500/10 text-[10px] font-bold uppercase tracking-widest text-indigo-200 hover:bg-indigo-500/20 disabled:opacity-40"
          >
            <Sparkle size={14} weight="bold" /> {busy === "ai" ? "…" : "AI match"}
          </Button>
        )}
      </div>
      {!compact && (
        <p className="text-xs text-zinc-500">
          Sync finds this account in QuickBooks by name, company or email — or creates the
          customer there if nothing matches.
        </p>
      )}
      {isAdmin && (
        <input
          data-testid="qbo-linker-search"
          value={qboQ}
          disabled={!on}
          onChange={(e) => searchQbo(e.target.value)}
          placeholder={on ? "Or search QuickBooks to link by hand…" : "Connect QuickBooks to search"}
          className={inputCls}
        />
      )}
      {qboResults.length > 0 && (
        <div className="max-h-40 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950">
          {qboResults.map((r) => (
            <button
              key={r.id}
              data-testid={`qbo-linker-result-${r.id}`}
              onClick={() => linkTo(r)}
              className="block w-full truncate px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-800"
            >
              {r.name}
            </button>
          ))}
        </div>
      )}
      {aiResult && (
        <div
          className="rounded-lg border border-indigo-500/20 bg-indigo-500/5 p-3"
          data-testid="qbo-linker-ai-result"
        >
          {(() => {
            const s = aiResult.suggestion || {};
            const best = (aiResult.candidates || []).find(
              (x) => String(x.id) === String(s.match_id),
            );
            return best ? (
              <>
                <p className="text-xs text-indigo-200/80">
                  AI suggests <span className="font-semibold text-indigo-100">{best.name}</span>
                  {typeof s.confidence === "number"
                    ? ` · ${Math.round(s.confidence * 100)}% confidence`
                    : ""}
                </p>
                {s.reason && <p className="mt-0.5 text-[11px] text-zinc-500">{s.reason}</p>}
                <button
                  data-testid="qbo-linker-ai-confirm"
                  onClick={() => linkTo(best)}
                  className="mt-2 rounded-md bg-emerald-500 px-3 py-1 text-xs font-semibold text-zinc-950 hover:bg-emerald-400"
                >
                  Confirm &amp; link
                </button>
              </>
            ) : (
              <p className="text-xs text-zinc-500">
                No confident match — pick one below, search, or Sync to create it.
              </p>
            );
          })()}
          {(aiResult.candidates || []).length > 0 && (
            <div className="mt-2 border-t border-zinc-800 pt-2">
              <p className="mb-1 text-[10px] uppercase tracking-widest text-zinc-500">
                Other candidates
              </p>
              {aiResult.candidates.map((cd) => (
                <button
                  key={cd.id}
                  data-testid={`qbo-linker-candidate-${cd.id}`}
                  onClick={() => linkTo(cd)}
                  className="block w-full truncate rounded px-2 py-1 text-left text-xs text-zinc-300 hover:bg-zinc-800"
                >
                  {cd.name}
                  {cd.company ? ` · ${cd.company}` : ""}
                  {cd.email ? ` · ${cd.email}` : ""}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
